import { includes } from 'lodash';
import React, { FunctionComponent, MouseEventHandler, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { gql, useMutation } from 'urql';
import { useCurrentUser } from '../../hooks';
import styled from '../../themes/themed-styled-components';
import { MutationResult, RoleName } from '../../types';
import { HacpackMutationApproveHandlingsArguments, Handling } from '../../types/graph';
import { QueryLoaderContext } from '../QueryLoader';
import { TerminalContext } from '../TerminalRoot';


const approveHandlingsMutation = gql<{ createOrUpdateHandlings: MutationResult }, HacpackMutationApproveHandlingsArguments>`
    mutation ($handlingIds: [Long!]!) {
        approveHandlings(handlingIds: $handlingIds)  {
            isOk
        }
    }
`;

const PrescriptionText = styled.pre`
white-space: pre-wrap
`;

export type Props = {
    handling: Handling;
}

const HandlingPrescriptions: FunctionComponent<Props> = ({ handling }) => {
    const { id, prescriptions, approvedByTerminal } = handling;
    const { t } = useTranslation('common');
    const { terminal } = useContext(TerminalContext);
    const { reload } = useContext(QueryLoaderContext);

    const { isSuperuser, hasRoleForTerminal } = useCurrentUser();

    const [approveResult, approve] = useMutation<{ approveHandlings: MutationResult }, HacpackMutationApproveHandlingsArguments>(approveHandlingsMutation);

    const { fetching: fetchingApproval } = approveResult;

    const canApprove = useMemo(() => {
        if (terminal === null || terminal.id === undefined || !includes(['TDT', 'SINTERMAR'], terminal?.name)) {
            return false;
        }

        return isSuperuser || hasRoleForTerminal(RoleName.OPERATIVE_MANAGER, terminal.id)
    }, [isSuperuser, hasRoleForTerminal, terminal]);

    const handleApproveClick = useCallback<MouseEventHandler>((ev) => {
        ev.preventDefault();

        if (id === undefined) {
            return;
        }
        approve({ handlingIds: [id] }).then(() => {
            reload();
        });
    }, [approve, id, reload])

    const approved = useMemo(() => approvedByTerminal === true || approveResult.data?.approveHandlings?.isOk === true, [approvedByTerminal, approveResult]);

    const approveButton = useMemo(() => {
        if (terminal === null || terminal.id === undefined || !includes(['TDT', 'SINTERMAR'], terminal?.name)) {
            return false;
        }

        if (approved === true) {
            return <Button disabled color='info'>{t('Visionate')}</Button>
        }

        if (canApprove) {
            return <Button disabled={fetchingApproval} color='primary' onClick={handleApproveClick}>{t('Approva')}</Button>
        }

        return <Button disabled color='success'>{t('Non ancora visionate')}</Button>

    }, [canApprove, t, handleApproveClick, approved, fetchingApproval, terminal]);



    if (prescriptions === undefined || prescriptions === null || prescriptions === "") {
        return null;
    }

    return <div>
        <div style={{ display: 'flex', gap: '.5em', alignItems: 'center' }}>
            <h4 style={approveButton !== null ? { margin: 0 } : undefined}>{t('Prescrizioni')}</h4>
            {approveButton}
        </div>
        <PrescriptionText>{prescriptions}</PrescriptionText>
    </div>;

};

export default HandlingPrescriptions;